/**
 * Send Email modal – matches Angular EmailBroadcastComponent UI.
 * Composes and sends email to the selected staff via /api/email_broadcast.
 * Email body uses CKEditor 4 (same approach as Angular `ckeditor4-angular` + full-all script).
 */
import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, X } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import modalStyles from '@/features/application/styles/modals.module.css'
import sendEmailStyles from '@/features/company/styles/sendEmailModal.module.css'
import { useAppSelector } from '@/app/hooks'
import { selectUser } from '@/features/auth/authSlice'
import apiClient from '@/lib/api/apiClient'
import { showSuccess, showError } from '@/lib/utils/notifications'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import { MAX_EMAIL_ATTACHMENT_MB } from '../constants'
import type { SendEmailModalProps } from '../types'

/** True when HTML has no visible text (CKEditor empty = `<p></p>`, etc.). */
function isEffectivelyEmptyEmailHtml(html: string): boolean {
    if (!html?.trim()) return true
    const text = html
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    return text.length === 0
}

const inputClass =
    'w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/30 focus:border-[rgb(21_151_255)]'

function ToggleButton({
    active,
    onClick,
    children,
    testId,
}: {
    active: boolean
    onClick: () => void
    children: React.ReactNode
    testId: string
}) {
    return (
        <button
            data-testid={testId}
            type="button"
            onClick={onClick}
            className={cn(
                sendEmailStyles.toggleBtn,
                active ? sendEmailStyles.toggleBtnActive : sendEmailStyles.toggleBtnInactive,
            )}
        >
            {children}
        </button>
    )
}

export function SendEmailModal({ staff, selectedUserIds, companyId, propertyId, onClose, onSuccess }: SendEmailModalProps) {
    const user = useAppSelector(selectUser)
    const [seperatedToAddress, setSeperatedToAddress] = useState<'0' | '1'>('0')
    const [replyTo, setReplyTo] = useState('')
    const [subject, setSubject] = useState('')
    const [body, setBody] = useState('')
    const [passthruMailgun, setPassthruMailgun] = useState<'0' | '1'>('0')
    const [sendSms, setSendSms] = useState(false)
    const [attachmentFiles, setAttachmentFiles] = useState<FileList | null>(null)
    const [sending, setSending] = useState(false)
    const [error, setError] = useState('')
    const fileInputRef = useRef<HTMLInputElement>(null)

    const recipientCount = selectedUserIds?.length ?? (staff?.user_id != null ? 1 : 0)

    /** Match Angular EmailBroadcast: prefill reply_to from logged-in user email. */
    useEffect(() => {
        if (user?.email) {
            setReplyTo((prev) => (prev.trim() === '' ? user.email : prev))
        }
    }, [user?.email])

    useEffect(() => {
        const prev = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = prev
        }
    }, [])

    const attachmentLabel = attachmentFiles?.length
        ? attachmentFiles.length === 1
            ? attachmentFiles[0].name
            : `${attachmentFiles.length} Files`
        : ''

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files
        if (!files?.length) {
            setAttachmentFiles(null)
            return
        }
        for (let i = 0; i < files.length; i++) {
            const sizeMB = files[i].size / 1024 / 1024
            if (sizeMB > MAX_EMAIL_ATTACHMENT_MB) {
                setError(`File size exceeds allowable limit of ${MAX_EMAIL_ATTACHMENT_MB} MB.`)
                setAttachmentFiles(null)
                e.target.value = ''
                return
            }
        }
        setError('')
        setAttachmentFiles(files)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        const trimmedSubject = subject.trim()
        if (!trimmedSubject) {
            setError('Email subject is required.')
            return
        }
        if (isEffectivelyEmptyEmailHtml(body)) {
            setError('Email body is required.')
            return
        }
        setSending(true)
        try {
            const rawIds =
                selectedUserIds && selectedUserIds.length > 0
                    ? selectedUserIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
                    : staff?.user_id != null
                      ? [Number(staff.user_id)]
                      : []
            const ids = [...new Set(rawIds)]
            if (ids.length === 0) {
                setError('No recipients selected.')
                setSending(false)
                return
            }

            const hasAttachment = attachmentFiles?.length
            const basePayload: Record<string, string | number | boolean> = {
                frompage: 'company_property',
                email_subject: trimmedSubject,
                email_body: body,
                seperated_to_address: seperatedToAddress,
                passthru_mailgun: passthruMailgun,
                send_sms: sendSms,
            }
            ids.forEach((uid, index) => {
                basePayload[`user_ids[${index}]`] = uid
            })
            if (propertyId != null && String(propertyId).trim() !== '') {
                basePayload.type = 'PROPERTY'
                basePayload.type_id = Number(propertyId)
            } else if (companyId != null && String(companyId).trim() !== '') {
                basePayload.type = 'COMPANY'
                basePayload.type_id = Number(companyId)
            }
            if (replyTo.trim()) basePayload.reply_to = replyTo.trim()

            const formData = new FormData()
            Object.entries(basePayload).forEach(([k, v]) => {
                if (v != null) {
                    formData.append(k, String(v))
                }
            })
            if (hasAttachment) {
                for (let i = 0; i < attachmentFiles!.length; i++) {
                    formData.append(`attachment[${i}]`, attachmentFiles![i])
                }
            }
            await apiClient.post('/api/email_broadcast', formData)
            showSuccess('Mail Sent Successfully!')
            onSuccess?.()
            onClose()
        } catch (err: unknown) {
            const e = err as { response?: { data?: unknown }; message?: unknown }
            const data = e?.response?.data
            const message =
                (data && typeof data === 'object' ? (data as { message?: unknown }).message : data) ??
                e?.message ?? 'Failed to send email.'
            showError(typeof message === 'string' ? message : 'Failed to send email.')
            setError(typeof message === 'string' ? message : 'Failed to send email.')
        } finally {
            setSending(false)
        }
    }

    const modal = (
        <div
            data-testid="company-send-email-overlay"
            className={`${modalStyles.modalOverlay} !z-[10001]`}
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-labelledby="send-email-modal-title"
        >
            <div
                className={`${modalStyles.modalContent} ${modalStyles.modalContent800} ${modalStyles.modalContentStacked}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={sendEmailStyles.header}>
                    <div className={sendEmailStyles.headerLead}>
                        <div>
                            <h3 id="send-email-modal-title" className={sendEmailStyles.title}>
                                Send Email
                            </h3>
                        </div>
                    </div>
                    <button
                        data-testid="company-send-email-close-button"
                        type="button"
                        onClick={onClose}
                        className={`${modalStyles.closeButton} ${modalStyles.modalValidationClose}`}
                        aria-label="Close"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                    <div className={sendEmailStyles.body}>
                        <div className={sendEmailStyles.recipientBanner}>
                            This email will be sent to{' '}
                            <strong>{recipientCount}</strong>{' '}
                            selected user{recipientCount !== 1 ? 's' : ''}.
                        </div>

                        <div className={sendEmailStyles.section}>
                            <h4 className={sendEmailStyles.sectionTitle}>Delivery</h4>
                            <div className={sendEmailStyles.field}>
                                <span className={sendEmailStyles.fieldLabel}>
                                    Send Separately <span className={sendEmailStyles.required}>*</span>
                                </span>
                                <div className={sendEmailStyles.toggleGroup}>
                                    <ToggleButton
                                        testId="company-send-email-send-separately-yes-button"
                                        active={seperatedToAddress === '1'}
                                        onClick={() => setSeperatedToAddress('1')}
                                    >
                                        Yes
                                    </ToggleButton>
                                    <ToggleButton
                                        testId="company-send-email-send-separately-no-button"
                                        active={seperatedToAddress === '0'}
                                        onClick={() => setSeperatedToAddress('0')}
                                    >
                                        No
                                    </ToggleButton>
                                </div>
                            </div>
                        </div>

                        <div className={sendEmailStyles.section}>
                            <h4 className={sendEmailStyles.sectionTitle}>Message</h4>

                            <div className={sendEmailStyles.field}>
                                <label htmlFor="send-email-reply" className={sendEmailStyles.fieldLabel}>
                                    Reply To
                                </label>
                                <input
                                    data-testid="company-send-email-reply-input"
                                    id="send-email-reply"
                                    type="email"
                                    value={replyTo}
                                    onChange={(e) => setReplyTo(e.target.value)}
                                    placeholder="Reply To"
                                    className={inputClass}
                                />
                            </div>

                            <div className={sendEmailStyles.field}>
                                <label htmlFor="send-email-subject" className={sendEmailStyles.fieldLabel}>
                                    Email Subject <span className={sendEmailStyles.required}>*</span>
                                </label>
                                <input
                                    data-testid="company-send-email-subject-input"
                                    id="send-email-subject"
                                    type="text"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    placeholder="Email Subject"
                                    required
                                    className={inputClass}
                                />
                            </div>

                            <div className={sendEmailStyles.field}>
                                <span className={sendEmailStyles.fieldLabel}>Attachment</span>
                                <div className={sendEmailStyles.attachmentRow}>
                                    <button
                                        type="button"
                                        data-testid="company-send-email-attachment-name-button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className={cn(
                                            sendEmailStyles.attachmentName,
                                            attachmentLabel && sendEmailStyles.attachmentNameHasFile,
                                        )}
                                    >
                                        {attachmentLabel || 'No file chosen'}
                                    </button>
                                    <input
                                        data-testid="company-send-email-attachment-input"
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        onChange={handleFileChange}
                                        className="hidden"
                                    />
                                    <button
                                        data-testid="company-send-email-attachment-upload-button"
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className={sendEmailStyles.uploadBtn}
                                    >
                                        Upload
                                    </button>
                                </div>
                                <div
                                    className="mt-2 bg-[#fef9ec] border border-[#eabf14] rounded-lg py-3 px-4 text-[#94845b] text-sm"
                                    role="alert"
                                >
                                    Use CTRL (windows)/CMD (apple) + Click to select multiple files to upload at one time.
                                </div>
                            </div>

                            <div className={sendEmailStyles.field}>
                                <span id="send-email-body-label" className={sendEmailStyles.fieldLabel}>
                                    Email Body <span className={sendEmailStyles.required}>*</span>
                                </span>
                                <div className={sendEmailStyles.editorWrap}>
                                    <CKEditor4
                                        id="send-email-body"
                                        value={body}
                                        onChange={setBody}
                                        config={{ height: 220 }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={sendEmailStyles.section}>
                            <h4 className={sendEmailStyles.sectionTitle}>Additional Options</h4>
                            <div className={sendEmailStyles.optionsPanel}>
                                <div className={sendEmailStyles.field}>
                                    <span className={sendEmailStyles.fieldLabel}>Passthru Mailgun</span>
                                    <div className={sendEmailStyles.toggleGroup}>
                                        <ToggleButton
                                            testId="company-send-email-passthru-mailgun-yes-button"
                                            active={passthruMailgun === '1'}
                                            onClick={() => setPassthruMailgun('1')}
                                        >
                                            Yes
                                        </ToggleButton>
                                        <ToggleButton
                                            testId="company-send-email-passthru-mailgun-no-button"
                                            active={passthruMailgun === '0'}
                                            onClick={() => setPassthruMailgun('0')}
                                        >
                                            No
                                        </ToggleButton>
                                    </div>
                                </div>

                                <label htmlFor="send-email-sms" className={sendEmailStyles.checkboxLabel}>
                                    <input
                                        data-testid="company-send-email-sms-checkbox"
                                        id="send-email-sms"
                                        type="checkbox"
                                        checked={sendSms}
                                        onChange={(e) => setSendSms(e.target.checked)}
                                    />
                                    Send Notification SMS
                                </label>
                            </div>
                        </div>

                        {error && (
                            <div
                                data-testid="company-send-email-error-message"
                                className={sendEmailStyles.errorBanner}
                            >
                                {error}
                            </div>
                        )}
                    </div>

                    <div className={sendEmailStyles.footer}>
                        <div className={`${sendEmailStyles.footerActions} ${modalStyles.modalFooterActionGroup}`}>
                            <button
                                data-testid="company-send-email-submit-button"
                                type="submit"
                                disabled={sending}
                                className={modalStyles.modalActionPrimary}
                            >
                                {sending ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Submit
                                    </>
                                ) : (
                                    'Submit'
                                )}
                            </button>
                            <button
                                data-testid="company-send-email-cancel-button"
                                type="button"
                                onClick={onClose}
                                className={modalStyles.modalActionSecondary}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )

    return createPortal(modal, document.body)
}
